<template>
    <transition name="redeem-confirm-fade">
        <div class="redeem-confirm" v-if="dialog" @click.self="dismiss">
            <div class="redeem-confirm-panel" role="dialog" aria-modal="true">
                <div class="redeem-confirm-body">
                    <h2 class="redeem-confirm-title">{{ t(titleKey) }}</h2>

                    <reward-card :reward="reward" :balance="balance" :is-logged="true" :tiers="tiers" />

                    <div class="redeem-confirm-usage" v-if="usageInstructions">
                        <span class="redeem-confirm-usage-label" v-lang="'dashboard-howToUse'"></span>
                        <p class="redeem-confirm-usage-body">{{ usageInstructions }}</p>
                    </div>

                    <p class="redeem-confirm-alert" v-if="status === 'error'">{{ errorMessage }}</p>
                </div>

                <p class="redeem-confirm-balance" v-if="selfUsable">
                    {{ t("dashboard-balanceAfter") }}
                    <span class="redeem-confirm-balance-value">{{ t("dashboard-rewardPoints", { points: balanceAfter }) }}</span>
                </p>

                <div class="redeem-confirm-actions" :class="{ 'is-single': !selfUsable }">
                    <template v-if="selfUsable">
                        <button
                            type="button"
                            class="redeem-confirm-button"
                            :disabled="status === 'loading'"
                            @click="closeModal"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                            <span v-lang="'dashboard-redeemCancel'"></span>
                        </button>

                        <button
                            type="button"
                            class="redeem-confirm-button redeem-confirm-button--primary"
                            :class="{ 'is-loading': status === 'loading' }"
                            :disabled="status === 'loading'"
                            @click="confirm"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span v-lang="'dashboard-redeemConfirm'"></span>
                        </button>
                    </template>

                    <button
                        v-else
                        type="button"
                        class="redeem-confirm-button redeem-confirm-button--close"
                        @click="closeModal"
                        v-lang="'dashboard-modalClose'"
                    ></button>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import { mapMutations } from "vuex";
import Api from "../../api";
import config from "../../config";
import RewardCard from "./points-reward-card";

export default {
    name: "RedeemConfirm",
    components: { RewardCard },
    props: {
        reward: {
            type: Object,
            default: () => ({}),
        },
        balance: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            status: "confirming",
            errorMessage: "",
        };
    },
    computed: {
        tiers() {
            return this.$store.state.dashboard.tiers || [];
        },
        dialog() {
            return this.$store.state.modal === "redeemConfirm";
        },
        balanceAfter() {
            return this.balance - (this.reward.points || 0);
        },
        selfUsable() {
            return this.reward.canUse !== false;
        },
        usageInstructions() {
            return this.reward.usageInstructions || "";
        },
        titleKey() {
            return this.selfUsable ? "dashboard-confirmRedeemTitle" : "dashboard-redeemStaffTitle";
        },
    },
    watch: {
        "reward.id"() {
            this.reset();
        },
        dialog() {
            this.reset();
        },
    },
    methods: {
        ...mapMutations(["closeModal"]),
        reset() {
            this.status = "confirming";
            this.errorMessage = "";
        },
        dismiss() {
            if (this.status !== "loading") {
                this.closeModal();
            }
        },
        redeemErrorMessage(key) {
            const terms = {
                "points-program-inactive": "dashboard-redeemErrorPointsProgramInactive",
                "staff-only-reward": "dashboard-redeemErrorStaffOnly",
                "reward-unavailable": "dashboard-redeemErrorRewardUnavailable",
                "recent-redemption": "dashboard-redeemErrorRecentRedemption",
                "insufficient-points": "dashboard-redeemErrorInsufficientPoints",
            };

            return this.t(terms[key] || "dashboard-redeemError");
        },
        confirm() {
            if (config.isPreview) {
                const now = new Date();
                const pad = (value) => String(value).padStart(2, "0");

                this.$store.commit("dashboard/setBalance", this.balanceAfter);
                this.$store.commit("dashboard/addTransaction", {
                    id: "preview-debit-" + now.getTime(),
                    type: "debit",
                    amount: this.reward.points || 0,
                    rewardName: this.reward.name || "",
                    date:
                        now.getFullYear() +
                        "-" + pad(now.getMonth() + 1) +
                        "-" + pad(now.getDate()) +
                        " " + pad(now.getHours()) +
                        ":" + pad(now.getMinutes()),
                });
                this.$emit("redeemed", {
                    reward: this.reward,
                    message: this.t("dashboard-redeemSuccessMessage"),
                });

                return;
            }

            this.status = "loading";

            Api.post("client/use-reward/" + this.reward.id)
                .then((response) => {
                    const body = response.body || {};

                    this.$emit("redeemed", {
                        reward: this.reward,
                        message: body.successMessage || this.t("dashboard-redeemSuccessMessage"),
                    });
                })
                .catch((error) => {
                    this.status = "error";
                    this.errorMessage = this.redeemErrorMessage(
                        error && error.body && error.body.message
                    );
                });
        },
    },
};
</script>

<style lang="scss">
.redeem-confirm {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(13, 18, 28, 0.55);
  overflow-y: auto;
}

.redeem-confirm-panel {
  width: 100%;
  max-width: 22.125rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1.5rem;
  border-radius: 1rem;
  background: #fcfcfd;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1.5rem 3rem rgba(0, 0, 0, 0.25);
}

.redeem-confirm-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.redeem-confirm-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.3;
  color: #1f2a37;
  text-align: center;
}

.redeem-confirm-balance {
  margin: 0;
  font-size: 1rem;
  line-height: 1.4;
  color: #384250;
  overflow-wrap: anywhere;
}

.redeem-confirm-balance-value {
  color: #2aaa4c;
}

.redeem-confirm-alert {
  margin: 0;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: #fee4e2;
  color: #b42318;
  font-size: 0.875rem;
  line-height: 1.4;
}

.redeem-confirm-usage {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: #f3f4f6;
}

.redeem-confirm-usage-label {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.3;
  color: #111927;
}

.redeem-confirm-usage-body {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #4d5761;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.redeem-confirm-actions {
  display: flex;
  gap: 1rem;
}

.redeem-confirm-actions.is-single {
  justify-content: center;
}

.redeem-confirm-button--close {
  flex: 0 1 auto;
  min-width: 12rem;
}

.redeem-confirm-button {
  flex: 1 1 0;
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: #fcfcfd;
  color: #1f2a37;
  font-size: 1rem;
  line-height: 1.2;
  cursor: pointer;
  transition: transform 0.1s;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;

    &:active {
      transform: none;
    }
  }
}

.redeem-confirm-button--primary {
  border-color: transparent;
  background: #2aaa4c;
  color: #f1fcf3;
}

.redeem-confirm-button.is-loading {
  position: relative;
  color: transparent;
  opacity: 1;

  svg {
    visibility: hidden;
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1.125rem;
    height: 1.125rem;
    margin: -0.5625rem 0 0 -0.5625rem;
    border: 2px solid rgba(255, 255, 255, 0.5);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: redeem-confirm-spin 0.8s linear infinite;
  }
}

@keyframes redeem-confirm-spin {
  to {
    transform: rotate(360deg);
  }
}

.redeem-confirm-fade-enter-active,
.redeem-confirm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.redeem-confirm-fade-enter,
.redeem-confirm-fade-leave-to {
  opacity: 0;
}
</style>
